import { useMemo } from 'react';
import { AlertTriangle, Pencil } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { Button } from '../../Shared/Button';
import {
  isMissingClockOut,
  isUnconfirmedComplete,
} from '../../../store/useTimeclockStore';
import { formatDate, parseIsoDate } from '../../../utils/dateUtils';
import type { FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto as TimeClockDay } from '../../../api/generated/models';
import { UnparkedVehiclesNotice } from './UnparkedVehiclesNotice';
import { RunningTripNotice } from './RunningTripNotice';

interface OpenDaysNoticeProps {
  /** Unconfirmed days from the store (`openDays`) — any date. */
  openDays: TimeClockDay[];
  todayIso: string;
  busy: boolean;
  /** Opens the correction for a day with a missing clock-out. */
  onEditDay: (entry: TimeClockDay) => void;
  /** Leads to the confirm flow; the unconfirmed-days hint is hidden without it. */
  onShowUnconfirmed?: () => void;
}

/**
 * Past days the employee still has to sort out (FACF-112): a forgotten clock-out blocks the
 * next clock-in, so those come first and loud; complete but unconfirmed days only get a line.
 * A trip still running from an earlier day and a vehicle left without a reading (FAFD-68) are
 * such open items too and come first.
 */
export function OpenDaysNotice({
  openDays,
  todayIso,
  busy,
  onEditDay,
  onShowUnconfirmed,
}: OpenDaysNoticeProps) {
  const { t } = useTranslation('timeclock');

  const missingClockOut = useMemo(
    () =>
      openDays
        .filter((day) => isMissingClockOut(day, todayIso))
        .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? '')),
    [openDays, todayIso],
  );
  const unconfirmedCount = useMemo(
    () => openDays.filter((day) => isUnconfirmedComplete(day, todayIso)).length,
    [openDays, todayIso],
  );

  const showUnconfirmed = Boolean(onShowUnconfirmed) && unconfirmedCount > 0;

  // empty:hidden — with nothing open, no gap is left in the page's stack.
  return (
    <div className="space-y-2 empty:hidden">
      <RunningTripNotice />
      <UnparkedVehiclesNotice />
      {missingClockOut.length > 0 && (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:bg-amber-900/10 dark:border-amber-800"
          role="status"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-amber-900 dark:text-amber-200">
                {t('openDays.missingClockOutTitle')}
              </p>
              <p className="text-[12px] text-amber-800 dark:text-amber-300 mt-0.5">
                {t('openDays.blocksClockIn')}
              </p>
            </div>
          </div>
          <ul className="mt-3 space-y-2">
            {missingClockOut.map((day) => {
              const date = day.date ?? '';
              return (
                <li
                  key={day.timeClockDayId ?? date}
                  className="flex flex-wrap items-center gap-2 rounded-lg bg-white/70 dark:bg-slate-900/40 px-3 py-2"
                >
                  <span className="flex-1 min-w-0 text-[13px] text-slate-900 dark:text-slate-100 tabular-nums">
                    {t('openDays.missingClockOut', { date: formatDayLabel(date) })}
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onEditDay(day)}
                    disabled={busy}
                    leftIcon={<Pencil className="w-3.5 h-3.5" strokeWidth={2} />}
                  >
                    {t('openDays.fixDay')}
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {showUnconfirmed && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 dark:bg-slate-900 dark:border-slate-700">
          <span className="flex-1 min-w-0 text-[12px] text-slate-600 dark:text-slate-300">
            {t('openDays.unconfirmed', { count: unconfirmedCount })}
          </span>
          <Button size="sm" variant="ghost" onClick={onShowUnconfirmed}>
            {t('openDays.showUnconfirmed')}
          </Button>
        </div>
      )}
    </div>
  );
}

/** "Mo, 28.09." — short enough for a phone row, with the weekday people remember shifts by. */
function formatDayLabel(dateIso: string): string {
  return formatDate(parseIsoDate(dateIso), { weekday: 'short', day: '2-digit', month: '2-digit' });
}
